/**
 * Zentraler UI-Zustand über @preact/signals. Hält Session, Server-Auswahl,
 * WS-Status und die Live-Daten (chat/players/status/flags/fbf) des aktiven
 * Servers. Bindet die api.ts/ws.ts-Clients ein: REST für Aktionen, WebSocket
 * für Live (kein Polling). Views lesen die Signals und re-rendern fein-granular.
 */

import { signal } from "@preact/signals";
import { createApiClient, type ApiResponse } from "./api";
import { createWsClient, type WsClient, type WsEvent, type WsStatus } from "./ws";
import { ACTIVE_SERVER_KEY, resolveActiveServer } from "./active-server";
import {
  appendDashboardSparkSample,
  emptyDashboardSparkHistory,
  type DashboardSparkHistory,
  type DashboardSparkSample,
} from "./dashboard-health";

export interface Session {
  user: string;
  name: string;
  role: "admin" | "user";
  permissions: string[];
}
export interface ServerChoice {
  id: string;
  name: string;
  host: string;
  port: number;
}
export interface ChatRecord {
  id: number;
  ts: number;
  tag: string;
  sender: string;
  message: string;
}

export interface ServerOverview {
  map: string;
  mode: string;
  players: number;
  maxPlayers: number;
  t1: number;
  t2: number;
  ts: number;
}

export const session = signal<Session | null>(null);
export const servers = signal<ServerChoice[]>([]);
export const activeServer = signal<string | null>(null);
export const wsStatus = signal<WsStatus>("closed");
/** Status aller (abonnierten) Server — fürs Dashboard. */
export const overview = signal<Record<string, ServerOverview>>({});
/**
 * Zeitpunkt (epoch ms) des letzten ECHTEN per-Server Worker/RCON-Pushs je Server.
 * Wird NUR in `onEvent` gesetzt, wenn für genau diesen Server ein WS-Event
 * (status/players) reinkommt — NICHT durch das globale Dashboard-Polling. So
 * altert "Last Signal" pro Server unabhängig und springt nur bei einem echten
 * Signal für diesen Server auf 0s zurück.
 */
export const serverSignalAt = signal<Record<string, number>>({});

/** Pure: setzt die Signalzeit für GENAU einen Server, lässt alle anderen
 * Einträge unverändert (kein pauschales Gleichstempeln). Testbar. */
export function applyServerSignal(map: Record<string, number>, id: string, atMs: number): Record<string, number> {
  return { ...map, [id]: atMs };
}

/** Stempelt das letzte Signal für genau einen Server (echter Push-Empfang). */
function markServerSignal(id: string, atMs = Date.now()): void {
  serverSignalAt.value = applyServerSignal(serverSignalAt.value, id, atMs);
}
/** Frontend-only Dashboard Sparkline-History. Bleibt bei Tabwechseln erhalten. */
export const dashboardSparkHistory = signal<DashboardSparkHistory>(emptyDashboardSparkHistory());

export function appendDashboardSparkHistory(sample: DashboardSparkSample, maxPoints = 60): void {
  dashboardSparkHistory.value = appendDashboardSparkSample(dashboardSparkHistory.value, sample, maxPoints);
}

export function resetDashboardSparkHistory(): void {
  dashboardSparkHistory.value = emptyDashboardSparkHistory();
}

function setOverview(id: string, o: ServerOverview): void {
  overview.value = { ...overview.value, [id]: o };
}

/** Live-Daten des AKTIVEN Servers (per WS gespeist). */
export const live = {
  players: signal<Record<string, unknown>[]>([]),
  status: signal<Record<string, unknown> | null>(null),
  flags: signal<Record<string, number> | null>(null),
  fbf: signal<{ on: boolean; reapply: boolean } | null>(null),
  chat: signal<ChatRecord[]>([]),
};

export const isAdmin = () => session.value?.role === "admin";
export const hasPermission = (permission: string) =>
  session.value?.role === "admin" || !!session.value?.permissions.includes(permission);

export const api = createApiClient({
  onUnauthorized: () => {
    session.value = null;
    ws?.close();
    ws = null;
  },
});

let ws: WsClient | null = null;

type WsLocation = Pick<Location, "protocol" | "hostname" | "host" | "port">;

export function buildWsUrl(loc: WsLocation, override?: string): string {
  if (override) return override;
  const proto = loc.protocol === "https:" ? "wss" : "ws";
  const isDirectDev = loc.port === "8787";
  const host = isDirectDev ? `${loc.hostname}:8789` : loc.host;
  return `${proto}://${host}/ws`;
}

function wsUrl(): string {
  // Caddy routet /ws auf den Worker-Dienst. Lokal ggf. WS_URL override.
  const override = (globalThis as { __WS_URL__?: string }).__WS_URL__;
  return buildWsUrl(location, override);
}

function resetLive(): void {
  live.players.value = [];
  live.status.value = null;
  live.flags.value = null;
  live.fbf.value = null;
  live.chat.value = [];
}

function onEvent(e: WsEvent): void {
  // Overview aller abonnierten Server (Dashboard) — unabhängig vom aktiven Server.
  if (e.type === "status") {
    // Echtes per-Server Signal: NUR dieser Server bekommt eine neue Signalzeit.
    markServerSignal(e.server);
    const d = e.data as Record<string, unknown>;
    setOverview(e.server, {
      map: String(d.map ?? ""), mode: String(d.mode ?? ""),
      players: Number(d.playerCount) || 0, maxPlayers: Number(d.maxPlayers) || 0,
      t1: Number(d.ticketsPac) || 0, t2: Number(d.ticketsEu) || 0, ts: Number(d.updatedAt) || 0,
    });
  } else if (e.type === "players") {
    markServerSignal(e.server);
    const arr = Array.isArray(e.data) ? (e.data as Record<string, unknown>[]) : [];
    const cur = overview.value[e.server];
    setOverview(e.server, { ...(cur ?? { map: "", mode: "", players: 0, maxPlayers: 0, t1: 0, t2: 0, ts: 0 }), players: arr.filter((p) => !p.bot).length });
  }

  // Live-Signale nur für den aktiven Server.
  if (e.server !== activeServer.value) return;
  switch (e.type) {
    case "players":
      live.players.value = Array.isArray(e.data) ? (e.data as Record<string, unknown>[]) : [];
      break;
    case "status":
      live.status.value = e.data as Record<string, unknown>;
      break;
    case "flags":
      live.flags.value = e.data as Record<string, number>;
      break;
    case "fbf":
      live.fbf.value = e.data as { on: boolean; reapply: boolean };
      break;
    case "chat": {
      const rec = e.data as ChatRecord;
      live.chat.value = [...live.chat.value.slice(-400), rec];
      break;
    }
  }
}

function connectWs(): void {
  if (ws) return;
  ws = createWsClient({ url: wsUrl(), onEvent, onStatus: (s) => (wsStatus.value = s) });
  if (activeServer.value) ws.subscribe(activeServer.value);
}

/** Abonniert mehrere Server-Topics (Dashboard). Stellt die WS-Verbindung sicher. */
export function subscribeServers(ids: string[]): void {
  connectWs();
  for (const id of ids) ws?.subscribe(id);
}

/** Hebt Abos wieder auf — der aktive Server bleibt abonniert. */
export function unsubscribeServers(ids: string[]): void {
  for (const id of ids) if (id !== activeServer.value) ws?.unsubscribe(id);
}

/** Lädt den Overview-Status der Server initial per REST (server_status). */
export async function loadOverview(ids: string[]): Promise<void> {
  for (const id of ids) {
    try {
      const r = await api.call<ApiResponse & { status: Record<string, unknown> | null }>("server_status", { server: id });
      if (r.ok && r.status) {
        const s = r.status;
        setOverview(id, {
          map: String(s.map ?? ""), mode: String(s.mode ?? ""),
          players: Number(s.players) || 0, maxPlayers: Number(s.maxPlayers) || 0,
          t1: Number(s.t1) || 0, t2: Number(s.t2) || 0, ts: Number(s.ts) || 0,
        });
      }
    } catch { /* ignorieren */ }
  }
}

/** localStorage best-effort (SSR/Privatmodus-sicher). */
function readStoredServer(): string | null {
  try { return localStorage.getItem(ACTIVE_SERVER_KEY); } catch { return null; }
}
function writeStoredServer(id: string | null): void {
  try {
    if (id) localStorage.setItem(ACTIVE_SERVER_KEY, id);
    else localStorage.removeItem(ACTIVE_SERVER_KEY);
  } catch { /* ignorieren */ }
}

async function loadServers(): Promise<void> {
  const r = await api.call<ApiResponse & { servers: ServerChoice[] }>("servers");
  servers.value = r.ok ? r.servers : [];
  // Reload-fest: gespeicherte Auswahl wieder matchen, sonst Fallback auf den ersten.
  const ids = servers.value.map((s) => s.id);
  const next = resolveActiveServer(ids, readStoredServer(), activeServer.value);
  if (next !== activeServer.value) activeServer.value = next;
  // Leere Liste (z.B. API-Fehler) NICHT die gespeicherte Präferenz löschen.
  if (next) writeStoredServer(next);
}

/** Wechselt den aktiven Server: Topic umschalten + Live-Daten zurücksetzen + persistieren. */
export function setActiveServer(id: string): void {
  const prev = activeServer.value;
  if (prev === id) return;
  activeServer.value = id;
  writeStoredServer(id);
  resetLive();
  if (ws) {
    if (prev) ws.unsubscribe(prev);
    ws.subscribe(id);
  }
}

/** Session beim Laden prüfen; bei gültiger Session App-Daten + WS aufbauen. */
export async function bootstrap(): Promise<void> {
  try {
    const s = await api.call<ApiResponse & Session>("session");
    if (s.ok) {
      session.value = {
        user: s.user,
        name: s.name,
        role: s.role,
        permissions: Array.isArray(s.permissions) ? s.permissions : [],
      };
      await loadServers();
      connectWs();
    }
  } catch {
    /* nicht eingeloggt -> Login */
  }
}

export async function login(user: string, password: string): Promise<ApiResponse> {
  const r = await api.call("login", { user, password });
  if (r.ok) {
    session.value = {
      user,
      name: String(r.name ?? user),
      role: (r.role as "admin" | "user") ?? "user",
      permissions: Array.isArray(r.permissions) ? r.permissions.map(String) : [],
    };
    await loadServers();
    connectWs();
  }
  return r;
}

export async function logout(): Promise<void> {
  try {
    await api.call("logout");
  } finally {
    session.value = null;
    activeServer.value = null;
    servers.value = [];
    resetLive();
    ws?.close();
    ws = null;
  }
}
